import React, { useState, useMemo } from 'react';
import { Card, Table, Button, Badge } from 'react-bootstrap';
import { FaSearch, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const cellStyle = { padding: '0.2rem 0.4rem', verticalAlign: 'middle', fontSize: '0.82rem' };
const nowrapCell = { ...cellStyle, whiteSpace: 'nowrap' };
const truncateCell = {
  ...cellStyle,
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const StatusBadge = ({ status }) => {
  const ativo = status === 0;
  return (
    <Badge
      variant={ativo ? 'success' : 'danger'}
      style={{ borderRadius: '12px', padding: '3px 10px', fontSize: '0.72rem', fontWeight: 500 }}
    >
      {ativo ? 'Ativo' : 'Inativo'}
    </Badge>
  );
};

const MobileCard = ({ item, isUser, onDetalhes }) => (
  <div
    className="mb-3"
    style={{
      borderRadius: '12px',
      border: '1px solid #e8ecf4',
      background: '#fff',
      boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
      overflow: 'hidden',
    }}
  >
    <div style={{ padding: '14px 16px' }}>
      {isUser ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8895a7', fontWeight: 600 }}>Cód.</span>{' '}
              <strong style={{ color: '#1a2332' }}>{item.codigo}</strong>
            </div>
            <StatusBadge status={item.status} />
          </div>
          <div className="mb-1">
            <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8895a7', fontWeight: 600, display: 'block' }}>Nome</span>
            <span style={{ fontWeight: 500, color: '#1a2332' }}>{item.nome}</span>
          </div>
          <div className="mb-1">
            <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8895a7', fontWeight: 600, display: 'block' }}>Login</span>
            <span style={{ color: '#1a2332' }}>{item.nome_usuario}</span>
          </div>
          {item.nome_grupo && (
            <div className="mb-1">
              <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8895a7', fontWeight: 600, display: 'block' }}>Grupo</span>
              <span style={{ color: '#1a2332' }}>{item.nome_grupo}</span>
            </div>
          )}
          {item.departamento && (
            <div className="mb-1">
              <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8895a7', fontWeight: 600, display: 'block' }}>Depto.</span>
              <span style={{ color: '#1a2332' }}>{item.departamento}</span>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="d-flex mb-2" style={{ gap: '1.5rem' }}>
            <div>
              <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8895a7', fontWeight: 600, display: 'block' }}>GE</span>
              <strong style={{ color: '#1a2332' }}>{item.ge}</strong>
            </div>
            <div>
              <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8895a7', fontWeight: 600, display: 'block' }}>Conta</span>
              <strong style={{ color: '#1a2332' }}>{item.conta}</strong>
            </div>
          </div>
          <div className="mb-1">
            <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#8895a7', fontWeight: 600, display: 'block' }}>Razão Social</span>
            <span style={{ fontWeight: 500, color: '#1a2332' }}>{item.nome}</span>
          </div>
        </>
      )}
    </div>
    <div style={{ borderTop: '1px solid #f0f2f5', padding: '10px 16px', backgroundColor: '#fafbfc' }}>
      <Button
        variant="outline-primary"
        size="sm"
        block
        onClick={() => onDetalhes(item)}
        style={{ borderRadius: '8px', fontWeight: 500 }}
      >
        <FaSearch className="mr-1" /> Detalhes
      </Button>
    </div>
  </div>
);

const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base', numeric: true });
};

const ResultsTable = ({ results, isUser, onDetalhes }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: null };
    });
  };

  const sortedResults = useMemo(() => {
    if (!results || !sortConfig.key || !sortConfig.direction) return results;
    const arr = [...results];
    arr.sort((a, b) => {
      const cmp = compararValores(a[sortConfig.key], b[sortConfig.key]);
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [results, sortConfig]);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort style={{ marginLeft: 4, fontSize: '0.7rem', opacity: 0.55 }} />;
    }
    return sortConfig.direction === 'asc' ? (
      <FaSortUp style={{ marginLeft: 4, fontSize: '0.75rem' }} />
    ) : (
      <FaSortDown style={{ marginLeft: 4, fontSize: '0.75rem' }} />
    );
  };

  const sortableTh = (label, key) => (
    <th
      onClick={() => handleSort(key)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title="Ordenar"
    >
      {label}
      {renderSortIcon(key)}
    </th>
  );

  if (!results || results.length === 0) return null;

  return (
    <Card style={{ borderRadius: '12px', border: '1px solid #e8ecf4', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
      <style>
        {`
          .consulta-acesso-mobile { display: none; }
          @media (max-width: 768px) {
            .consulta-acesso-desktop { display: none !important; }
            .consulta-acesso-mobile { display: block; }
          }
          .consulta-acesso-table th {
            font-size: 0.78rem;
            padding: 0.3rem 0.4rem;
            white-space: nowrap;
          }
          .consulta-acesso-scroll {
            max-height: 55vh;
            overflow-y: auto;
          }
          .consulta-acesso-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background-color: #0747a6;
            color: #fff;
            font-weight: 600;
            border-bottom: 2px solid #063682;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }
          .consulta-acesso-table tbody tr {
            transition: background-color 0.15s ease;
          }
          .consulta-acesso-table tbody tr:hover {
            background-color: #eef2f9;
          }
        `}
      </style>
      <div className="consulta-acesso-desktop consulta-acesso-scroll">
        <Table bordered hover size="sm" className="mb-0 consulta-acesso-table">
          <thead>
            <tr>
              {isUser ? (
                <>
                  {sortableTh('Cód.', 'codigo')}
                  {sortableTh('Nome', 'nome')}
                  {sortableTh('Login', 'nome_usuario')}
                  {sortableTh('Status', 'status')}
                  {sortableTh('Grupo', 'nome_grupo')}
                  {sortableTh('Depto.', 'departamento')}
                </>
              ) : (
                <>
                  {sortableTh('GE', 'ge')}
                  {sortableTh('Conta', 'conta')}
                  {sortableTh('Razão Social', 'nome')}
                </>
              )}
              <th style={{ width: '70px', textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {sortedResults.map((item) => (
              <tr key={item.identificador} style={{ lineHeight: '1' }}>
                {isUser ? (
                  <>
                    <td style={nowrapCell}>{item.codigo}</td>
                    <td style={truncateCell} title={item.nome}>{item.nome}</td>
                    <td style={nowrapCell}>{item.nome_usuario}</td>
                    <td style={{ ...nowrapCell, textAlign: 'center' }}>
                      <StatusBadge status={item.status} />
                    </td>
                    <td style={truncateCell} title={item.nome_grupo}>{item.nome_grupo || '-'}</td>
                    <td style={truncateCell} title={item.departamento}>{item.departamento || '-'}</td>
                  </>
                ) : (
                  <>
                    <td style={nowrapCell}>{item.ge}</td>
                    <td style={nowrapCell}>{item.conta}</td>
                    <td style={cellStyle}>{item.nome}</td>
                  </>
                )}
                <td style={{ textAlign: 'center', ...nowrapCell }}>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => onDetalhes(item)}
                    style={{ color: '#0747a6', fontSize: '0.9rem', padding: '2px 8px' }}
                    title="Ver detalhes"
                  >
                    <FaSearch />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="consulta-acesso-mobile p-2">
        {sortedResults.map((item) => (
          <MobileCard key={item.identificador} item={item} isUser={isUser} onDetalhes={onDetalhes} />
        ))}
      </div>
    </Card>
  );
};

export default ResultsTable;
